import { MediaDownloadDto, MediaUploadDto, TubePostDownloadDto, valuesWikiColumnType, WikiColumnDownloadDto, WikiColumnType, WikiTableCellDownloadDto, WikiTableDownloadDto, WikiTableRowDownloadDto } from 'collaboration-service';
import { MediaType } from 'components/MediaSelection/FileSelector';
import MediaSelection from 'components/MediaSelection/MediaSelection';
import { BottomButtonQ } from 'components/Posts/PostClasses';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import TubeVideoSelection from 'components/TubeVideoSelection/TubeVideoSelection';
import WikiEditHotspot, { ExtendedOverlayData } from 'components/Wiki/WikiEditHotspot';
import WikiEditTable from 'components/Wiki/WikiEditTable';
import WikiTableEditSelector from 'components/Wiki/WikiTableEditSelector';
import Editor from "Dummies/TestImpl/TestEditor";
import { Button, ButtonGroup, FormElement, Icon, Image, ImgSelect, Input, useBelowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { ComponentWithMappedApplicationState } from 'services/ApplicationState/HelperInterfaces';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { getTranslated, getTranslatedExact } from 'services/Helpers/TranslationHelpers';
import { ImgI18NTranslatedComponentProps, translate } from 'services/ImgI18N';
import { UploadFile } from './WikiEditArticle';
import WikiHotSpot from './WikiHotSpot';
import { WikiSC as T } from './WikiSC';

const elementResizeDetectorMaker = require("element-resize-detector");
const erdUltraFast = elementResizeDetectorMaker({
    strategy: "scroll" //<- For ultra performance.
});


interface TypeOption {
    value: WikiColumnType;
    label: string;
}

const types: TypeOption[] = _.map(valuesWikiColumnType, v => ({ value: v as WikiColumnType, label: v }));
type ColumnTypes = "column" | "full";

const mapper = (state: ApplicationState) => ({ contentLanguage: state.contentLanguage, referenceLanguage: state.wikiState.referenceLanguage })
export interface WikiEditColumnProps extends ImgI18NTranslatedComponentProps, ComponentWithMappedApplicationState<typeof mapper> {
    delete?: (column: WikiColumnDownloadDto) => void;
    column: WikiColumnDownloadDto;
    moveLeft?: (column: WikiColumnDownloadDto) => void;
    moveRight?: (column: WikiColumnDownloadDto) => void;
    columnCount: number;
    columnChanged: (column: WikiColumnDownloadDto, uploadFile?: UploadFile | UploadFile[]) => void;
    showReference: boolean;
    referenceColumn?: WikiColumnDownloadDto;
    reviewMode?: boolean;
    language?: string;
    activeEditor?: boolean;
}
export interface WikiEditColumnState {
    showPreviewImageChapter: boolean;
    activated: boolean;
    width: number;
    imageAsHotspot: boolean;
}

const WikiEditColumn = (props: WikiEditColumnProps) => {
    const {
        column,
        referenceColumn,
        reviewMode,
        contentLanguage,
        language,
        referenceLanguage,
        currentLanguage,
        t,
        delete: deleteProp,
        moveLeft: moveLeftProp,
        moveRight: moveRightProp,
        columnCount,
        columnChanged,
        showReference,
        activeEditor
    } = props;


    const [activated, setActivated] = React.useState<boolean>(false);
    const [width, setWidth] = React.useState<number>(0);
    const [imageAsHotspot, setImageAsHotspot] = React.useState<boolean>(false);

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const hrefPpeFix = React.useCallback((s?: string): string => {
        if (s === undefined) return " ";
        if (!s.includes('href')) return s;
        const re = /<a href="\/#\/ppe\/article\/(.+?)".?>/gi;
        // eslint-disable-next-line no-useless-escape        
        const newstr = s.replace(re, "<a class=\"ppe-link\" href=\"\/#/ppe/article/$1\" rel=\"noopener noreferrer\" data-id=\"$1\">");
        return newstr;
    }, []);

    const {
        columnText,
        media,
        id,
        refHeadline,
        headline,
        overlayData,
        tableContent,
        showTableEditor,
        isOverlayImage,
        imgWidth,
        imgHeight,
        aspect,
        revisionMode,
        uri
    } = React.useMemo(() => {
        const columnText = hrefPpeFix(getTranslatedExact(column.textContent, language ?? contentLanguage)?.text);
        const media = getTranslatedExact(column.mediaContent, language ?? contentLanguage);
        const columnType = column.columnType;
        const id = _.findIndex(types, t => t.label === columnType);
        const refHeadline = getTranslatedExact((referenceColumn ?? column).title, referenceLanguage)?.text ?? "";
        const headline = getTranslatedExact(column.title, language ?? contentLanguage)?.text ?? "";
        const overlayData = getTranslatedExact(column.mediaOverlay, language ?? contentLanguage) ?? { lng: language ?? contentLanguage ?? currentLanguage, elements: [] };
        const tableContent = getTranslatedExact(column.tableContent, language ?? contentLanguage);
        const showTableEditor = tableContent && (tableContent.rows.length > 0 || tableContent.header.length > 0);
        const isOverlayImage = column.mediaOverlay.length > 0;

        const imgWidth = media?.width && media?.height ? media.width : 400;
        const imgHeight = media?.width && media?.height ? media.height : 280;
        const aspect = imgWidth / imgHeight;
        const revisionMode = reviewMode ?? false;

        const uri = _.find(getTranslatedExact(column.mediaContent, referenceLanguage)?.links, l => l.ref === "self")?.uri;

        return { columnText, media, id, refHeadline, headline, overlayData, tableContent, showTableEditor, isOverlayImage, imgWidth, imgHeight, aspect, revisionMode, uri };
    }, [hrefPpeFix, column, language, contentLanguage, currentLanguage, referenceColumn, referenceLanguage, reviewMode]);

    const { setRef, activateColumn, inactivateColumn, toggleHotSpot } = React.useMemo(() => {
        const setRef = (ref?: HTMLDivElement | null) => {
            if (!ref) return;
            try { erdUltraFast.removeAllListeners(ref); } catch { /* noop */ }
            containerRef.current = ref;
            erdUltraFast.listenTo({}, ref, (ele: HTMLDivElement) => {
                setWidth(Math.floor(ele?.offsetWidth ?? 0));
            });
        };
        const activateColumn = () => setActivated(true);
        const inactivateColumn = () => setActivated(false);
        const toggleHotSpot = () => setImageAsHotspot(v => !v);
        return { setRef, activateColumn, inactivateColumn, toggleHotSpot };
    }, []);

    const { onDelete, moveLeft, moveRight } = React.useMemo(() => {
        const onDelete = () => { if (deleteProp) deleteProp(column); };
        const moveLeft = () => { if (moveLeftProp) moveLeftProp(column); };
        const moveRight = () => { if (moveRightProp) moveRightProp(column); };
        return { onDelete, moveLeft, moveRight };
    }, [deleteProp, moveLeftProp, moveRightProp, column]);

    const { changeColumnType, setColumnsLayout, videoSelected } = React.useMemo(() => {
        const changeColumnType = (value: TypeOption) => {
            const col = _.cloneDeep(column);
            col.columnType = value.value;
            columnChanged(col);
            setActivated(false);
        };
        const setColumnsLayout = (columnWidth: ColumnTypes) => () => {
            const changedColumn = _.cloneDeep(column);
            changedColumn.columnWidth = columnWidth === "column" ? 1 : columnCount;
            columnChanged(changedColumn);
        };
        const videoSelected = (video: TubePostDownloadDto) => {
            if (column) {
                const col = _.clone(column);
                const upFiles: UploadFile[] = [];
                _.forEach(video.content, c => {
                    if (c.useContent) {
                        const m = { ...c.content, copyByReference: true, changed: true } as any;
                        const idx = _.findIndex(col.mediaContent, mc => mc.lng === c.lng);
                        if (idx >= 0) col.mediaContent[idx] = { ...m, lng: c.lng ?? "en-GB" } as MediaDownloadDto;
                        else col.mediaContent.push({ ...m, lng: c.lng ?? "en-GB" } as MediaDownloadDto);
                    }
                    upFiles.push({ columnId: col.id, lng: c.lng ?? "en-GB" });
                });
                columnChanged(col, upFiles);
            }
        };
        return { changeColumnType, setColumnsLayout, videoSelected };
    }, [column, columnCount, columnChanged]);

    const lng = language ?? contentLanguage ?? currentLanguage;

    const { saveOverlayData, onMediaSelected, onDeleteMedia, setHeadline, setTextContent, setTableContent, generate } = React.useMemo(() => {
        const saveOverlayData = (extendedOD: ExtendedOverlayData) => {
            if (column) {
                const overlay = { ...extendedOD, file: undefined } as any;
                const col = _.clone(column);
                const idx = _.findIndex(col.mediaOverlay, mo => mo.lng === lng);
                overlay.lng = lng;
                if (idx !== -1) col.mediaOverlay[idx] = overlay; else col.mediaOverlay.push(overlay);
                let uploadFile: UploadFile | undefined = undefined;
                _.forEach(extendedOD.elements, (e, i) => {
                    if (e.file) {
                        uploadFile = {
                            mediaFile: e.file,
                            media: { ...e.media, links: [] } as any,
                            columnId: column.id,
                            lng: lng,
                            overlayIndex: i,
                        };
                    } else if (e.media?.changed) {
                        uploadFile = {
                            deleteMe: true,
                            mediaFile: undefined,
                            media: { changed: true } as MediaUploadDto,
                            columnId: column.id,
                            lng: lng,
                            overlayIndex: i,
                        };
                    }
                    e.file = undefined as any;
                });
                columnChanged(col, uploadFile);
            }
        };
        const onMediaSelected = async (mediaFile: File, mediaU: MediaUploadDto) => {
            const uploadFile: UploadFile = {
                mediaFile,
                media: mediaU,
                columnId: column.id,
                lng: lng,
            };
            if (column) {
                const col = _.clone(column);
                const idx = _.findIndex(col.mediaContent, mc => mc?.lng === uploadFile.lng);
                if (idx >= 0) col.mediaContent[idx] = { ...mediaU, lng: uploadFile.lng ?? "en-GB" } as MediaDownloadDto;
                else col.mediaContent.push({ ...mediaU, lng: uploadFile.lng ?? "en-GB" } as MediaDownloadDto);
                columnChanged(col, uploadFile);
            }
        };
        const onDeleteMedia = () => {
            if (column) {
                const col = _.clone(column);
                const mediaIndex = _.findIndex(col.mediaContent, m => m?.lng === lng);
                if (mediaIndex >= 0) {
                    const uploadFile: UploadFile = { deleteMe: true, columnId: col.id, lng: lng };
                    col.mediaContent.splice(mediaIndex, 1);
                    columnChanged(col, uploadFile);
                }
            }
        };
        const setHeadline = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const title = event.target.value;
            const col = _.cloneDeep(column);
            const i = _.findIndex(col.title, t => t.lng === lng);
            if (i >= 0) col.title[i].text = title; else col.title.push({ lng, text: title });
            columnChanged(col);
        };
        const setTextContent = (html: string) => {
            const col = _.cloneDeep(column);
            const i = _.findIndex(col.textContent, t => t.lng === lng);
            if (i >= 0) col.textContent[i].text = html; else col.textContent.push({ lng, text: html });
            columnChanged(col);
        };
        const setTableContent = (table: WikiTableDownloadDto) => {
            const changedColumn = _.cloneDeep(column);
            const i = _.findIndex(changedColumn.tableContent, t => t.lng === lng);
            if (i >= 0) changedColumn.tableContent[i] = table; else { table.lng = lng; changedColumn.tableContent.push(table); }
            columnChanged(changedColumn);
        };
        const generate = (rows: number, columns: number) => {
            const header: Array<WikiTableCellDownloadDto> = Array<WikiTableCellDownloadDto>();
            const rr = Array<WikiTableRowDownloadDto>();
            for (let i = 0; i < columns; i++) header.push({ content: "", colSpan: 1, rowSpan: 1 });
            for (let i = 0; i < rows; i++) {
                const cells = Array<WikiTableCellDownloadDto>();
                for (let j = 0; j < columns; j++) cells.push({ content: "", colSpan: 1, rowSpan: 1 });
                rr.push({ cells });
            }
            const tableContentGenerated: WikiTableDownloadDto = { lng, header, rows: rr };
            setTableContent(tableContentGenerated);
        };
        return { saveOverlayData, onMediaSelected, onDeleteMedia, setHeadline, setTextContent, setTableContent, generate };
    }, [column, lng, columnChanged]);

    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    const contentWidth = useThemePart(t => t.content.width);
    const isTablet = useBelowBreakpointOrEqual("tablet")

    return (
        <T.ColumnContainer ref={setRef} activated={activated} revisionMode={revisionMode}>
            <div style={{ padding: revisionMode ? 0 : "10px 10px 41px 10px" }}>
                {showReference && (
                    <T.WECReferenceContent revisionMode={revisionMode}>
                        <h3>{t("reference:")}{refHeadline}</h3>
                        <div className="revisionModeContent">
                            {column.columnType === "Text" && (
                                <SafeHTML html={getTranslated((referenceColumn ?? column).textContent, referenceLanguage).text} />
                            )}
                        </div>
                        {column.columnType === "Table" && (
                            <div style={{ marginTop: 13 }}>
                                <WikiEditTable noEdit table={getTranslated((referenceColumn ?? column).tableContent, referenceLanguage)} onChange={setTableContent} />
                            </div>
                        )}
                        {column.columnType === "Image" && (
                            <T.ColumnContentImage imgWidth={getTranslated((referenceColumn ?? column).mediaContent, referenceLanguage).width} ref={setRef}>
                                {isOverlayImage && overlayData ? (
                                    <T.HotSpotContainer aspect={aspect} width={isTablet ? 320 : 600}>
                                        <T.HotSpotContainerImage>
                                            <Image src={getMediaLink((referenceColumn ?? column), y => getTranslated(y.mediaContent, referenceLanguage)) ?? emptyImageUrl} />
                                        </T.HotSpotContainerImage>
                                        {_.map(overlayData.elements, (e, i) => {
                                            const delay = (i + 1) * 0.5;
                                            return (
                                                <WikiHotSpot key={i} element={e} delay={delay} imgWidth={imgWidth ?? 400} imgHeight={imgHeight ?? 280} width={width} offsetY={0} />
                                            );
                                        })}
                                    </T.HotSpotContainer>
                                ) : (
                                    <Image src={getMediaLink((referenceColumn ?? column), y => getTranslated(y.mediaContent, referenceLanguage)) ?? emptyImageUrl} modalable />
                                )}
                            </T.ColumnContentImage>
                        )}
                        {column.columnType === "Video" && (
                            <div style={{ width: "100%", display: "block" }}>
                                <div style={{ maxWidth: "320px", width: "100%" }}>
                                    <video src={uri} controls preload="auto" width="100%" style={{ outline: "none" }} crossOrigin="anonymous">
                                        {_.map(media?.subtitles, (s) => {
                                            const l = _.find(s.links, l => l.ref === "self");
                                            const lng = (s.lng ?? "").split("-")[0];
                                            const label = t(`general::${lng}`);
                                            const sdef = getTranslated(media?.subtitles, contentLanguage);
                                            if (l && label) {
                                                return <track key={s.reference} src={l?.uri} label={label} kind="captions" srcLang={lng} default={sdef !== undefined && sdef.lng === s.lng} />;
                                            }
                                            return null;
                                        })}
                                    </video>
                                </div>
                            </div>
                        )}
                    </T.WECReferenceContent>
                )}
                {revisionMode ? (
                    <>
                        <Input kind={activeEditor === false ? "cancelButtonStyle" : "default"} width="100%" value={headline} onChange={setHeadline} placeHolder={t("headline (optional)")} disabled={activeEditor === false} />

                    </>
                ) : (
                    <div style={{ marginBottom: -1 }}>
                        <FormElement noMargin element={Input} kind="default" width="100%" value={headline} onChange={setHeadline} placeHolder={t("headline (optional)")} />
                    </div>
                )}
                {column.columnType === "Text" && (
                    revisionMode ?
                        <T.EditorContainer revisionMode={revisionMode}>
                            {(activeEditor === true || activeEditor === undefined) ?
                                <>
                                    <Editor value={columnText} onChange={setTextContent} />
                                </>
                                :
                                <T.InactiveEditorContainer>
                                    <SafeHTML html={columnText} />
                                </T.InactiveEditorContainer>
                            }
                        </T.EditorContainer>
                        :
                        <T.EditorContainer revisionMode={revisionMode}>
                            <Editor value={columnText} onChange={setTextContent} />
                        </T.EditorContainer>

                )}
                {column.columnType === "Image" && (
                    <div style={{ position: "relative" }}>
                        {imageAsHotspot ? (
                            <WikiEditHotspot overlayData={overlayData} imgWidth={imgWidth} imgHeight={imgHeight} overlayDataChanged={saveOverlayData} offsetY={0} width={width}>
                                <MediaSelection onMediaSelected={onMediaSelected} onDelete={onDeleteMedia} uploadType={media?.mediaType === 'image/svg+xml' ? MediaType.SVG : MediaType.IMAGE} backgroundColor="transparent" media={media} marginTop={0} showPlaceholderImage hotSpotMode />
                            </WikiEditHotspot>
                        ) : (
                            <div style={{ margin: "0 0.25rem" }}>
                                <MediaSelection
                                    onMediaSelected={onMediaSelected}
                                    onDelete={onDeleteMedia}
                                    uploadType={media?.mediaType === 'image/svg+xml' ? MediaType.SVG : MediaType.IMAGE}
                                    backgroundColor="transparent"
                                    media={media}
                                    marginTop={0}
                                    showPlaceholderImage
                                    additionalButtons={[{ kind: "halfTransparentButton", icon: "hotspot", onClick: toggleHotSpot, floated: "left", content: t("show hotspot edit mode") }]}
                                />
                            </div>
                        )}
                        {imageAsHotspot && (
                            <T.CloseButton>
                                <Button kind='halfTransparentButton' onClick={toggleHotSpot} icon="times" floated="right" tooltip={{ tooltipText: t("hide hotspot edit mode") }} />
                            </T.CloseButton>
                        )}
                    </div>
                )}
                {column.columnType === "Audio" && (
                    <MediaSelection onMediaSelected={onMediaSelected} onDelete={onDeleteMedia} imgAspect={(16 / 9)} uploadType={MediaType.AUDIO} backgroundColor="transparent" showPlaceholderImage hideButtonText media={media} />
                )}
                {column.columnType === "Video" && (
                    <div style={{ padding: 5 }}>
                        <MediaSelection onMediaSelected={onMediaSelected} onDelete={onDeleteMedia} imgAspect={(16 / 9)} uploadType={MediaType.VIDEO} backgroundColor="transparent" showPlaceholderImage hideButtonText media={media}>
                            {(p) => <TubeVideoSelection selectProps={p} videoSelected={videoSelected} />}
                        </MediaSelection>
                    </div>
                )}
                {column.columnType === "Table" && (
                    <div style={{ margin: "0px 4px" }}>
                        {tableContent && showTableEditor ? (
                            <div style={{ maxWidth: (contentWidth / (column.columnWidth > 1 ? 1 : columnCount) - 93) }}>
                                <WikiEditTable table={tableContent} onChange={setTableContent} />
                            </div>
                        ) : (
                            <>
                                <T.TableSelectorInfo>
                                    <Icon name="info" style={{ float: "left", marginRight: 10 }} />
                                    {t("first select the dimensions of the table")}
                                </T.TableSelectorInfo>
                                <T.TableSelectorBox>
                                    <WikiTableEditSelector columns={9} rows={7} cellWidth="24px" cellHeight="24px" selected={generate} />
                                </T.TableSelectorBox>
                            </>
                        )}
                    </div>
                )}
            </div>
            {!revisionMode && (
                <T.SCHeaderContainer activated={activated}>
                    <BottomButtonQ>
                        <T.ButtonMore icon="more" kind={activated ? "primary" : "transparentButton"} size="default" padding="0px" onClick={activated ? inactivateColumn : activateColumn} />
                    </BottomButtonQ>
                    {activated && (
                        <>
                            <T.WikiSelectBox>
                                <ImgSelect options={types} defaultValue={types[id]} onChange={changeColumnType} />
                            </T.WikiSelectBox>
                            <T.WikiMoreOptionsBox>
                                <ButtonGroup vertical>
                                    {columnCount !== 1 && _.map(["column", "full"], (v, l) => {
                                        const isActive = (column.columnWidth !== 1 || v === "column") && (column.columnWidth === 1 || v === "full");
                                        return (
                                            <Button icon={v === "column" ? "decrease" : "expand"} kind="transparentButton" onClick={setColumnsLayout(v as ColumnTypes)} key={l} active={isActive} />
                                        );
                                    })}
                                    <Button icon="delete" kind="transparentButton" onClick={onDelete} />
                                    {(moveRightProp || moveLeftProp) && (
                                        <>
                                            {moveLeftProp && <Button icon="chevron left" onClick={moveLeft} kind="transparentButton" />}
                                            {moveRightProp && <Button icon="chevron right" onClick={moveRight} kind="transparentButton" />}
                                        </>
                                    )}
                                </ButtonGroup>
                            </T.WikiMoreOptionsBox>
                        </>
                    )}
                </T.SCHeaderContainer>
            )}
        </T.ColumnContainer>
    );
};

export default translate("wiki")(connect(mapper)(WikiEditColumn));

